import { useEffect, useRef, useState } from 'react'
import { useCopy } from '../../content'
import { useSede } from '../../sede'
import { Eyebrow } from '../layout/Eyebrow'
import { emphasize } from './emphasize'

/**
 * Chi Siamo — Vercelli's manifesto, copied from src/components/Manifesto.tsx and
 * retokened onto the `surface` tone (cream in light, forest in dark). The text IS
 * the design: statement, lead, «I nostri valori» over three pillars, the founder line. The rose corners
 * (brass at 8 %) and the radial glow are the old site's; the reveals stay
 * IntersectionObserver-based.
 */
export function Manifesto() {
  const copy = useCopy()
  const sede = useSede()
  const sectionRef = useRef<HTMLElement>(null)
  const [isVisible, setIsVisible] = useState(false)

  useEffect(() => {
    const el = sectionRef.current
    if (!el) return
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setIsVisible(true); obs.disconnect() } },
      { threshold: 0.15 },
    )
    obs.observe(el)
    return () => obs.disconnect()
  }, [])

  const pillars = copy.manifesto.pillars.map((p) => <Pillar key={p.title} word={p.title} text={p.text} />)

  return (
    <section
      ref={sectionRef}
      id="chi-siamo"
      className="relative overflow-hidden min-h-[100dvh] flex items-center bg-vc2-surface text-vc2-ink"
    >
      {/* Subtle radial glow behind the text */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: 'radial-gradient(ellipse 60% 50% at 50% 45%, rgba(201,168,117,0.06) 0%, transparent 70%)',
        }}
      />

      {/* ── Corner floral bouquets ─────────────────────── */}
      <RoseBouquet className="absolute top-4 left-4 sm:top-8 sm:left-8 w-24 sm:w-32 lg:w-40" />
      <RoseBouquet className="absolute top-4 right-4 sm:top-8 sm:right-8 w-24 sm:w-32 lg:w-40 -scale-x-100" />
      <RoseBouquet className="absolute bottom-4 left-4 sm:bottom-8 sm:left-8 w-24 sm:w-32 lg:w-40 -scale-y-100" />
      <RoseBouquet className="absolute bottom-4 right-4 sm:bottom-8 sm:right-8 w-24 sm:w-32 lg:w-40 -scale-x-100 -scale-y-100" />

      <div className="relative max-w-5xl mx-auto px-6 sm:px-10 py-16 sm:py-0 w-full">

        {/* ── Eyebrow ──────────────────────────────────── */}
        <div
          className={`mb-6 sm:mb-10 transition-all duration-700 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
          }`}
        >
          <Eyebrow className="vc2-kicker text-vc2-accent text-center">{copy.manifesto.eyebrow}</Eyebrow>
        </div>

        {/* ── Mission statement ─────────────────────────── */}
        <h2
          className={`vc2-display text-center leading-[1.12] mb-6 sm:mb-8 transition-all duration-1000 delay-200 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
          style={{
            fontSize: 'clamp(1.85rem, 4.5vw, 2.75rem)',
            letterSpacing: '0.01em',
            fontWeight: 400,
          }}
        >
          {emphasize(copy.manifesto.statement)}
        </h2>

        <p
          className={`text-center text-vc2-ink-soft text-base sm:text-lg leading-relaxed max-w-2xl mx-auto mb-10 sm:mb-12 transition-all duration-1000 delay-400 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          {copy.manifesto.lead}
        </p>

        {/* ── Divider ──────────────────────────────────── */}
        <div
          className={`mx-auto h-px max-w-xs mb-10 sm:mb-12 origin-center transition-transform duration-[1.5s] delay-500 ${
            isVisible ? 'scale-x-100' : 'scale-x-0'
          }`}
          style={{ background: 'linear-gradient(90deg, transparent, rgb(201 168 117), transparent)' }}
        />

        {/* ── Pillars: infinite marquee on mobile, grid on sm+ ── */}
        <div
          className={`transition-all duration-1000 delay-700 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          {/* «I nostri valori» — subtitle over the three pillars */}
          <h3 className="vc2-kicker text-vc2-accent text-center mb-6 sm:mb-8">
            {copy.manifesto.pillarsTitle}
          </h3>

          {/* Desktop: normal grid */}
          <div className="hidden sm:grid sm:grid-cols-3 gap-6 lg:gap-10">
            {pillars}
          </div>

          {/* Mobile: infinite marquee. `marquee-viewport` keeps the
              strip reachable under prefers-reduced-motion (the global
              reduced-motion block stops the animation and switches
              this container to overflow-x auto). */}
          <div className="marquee-viewport sm:hidden -mx-6 overflow-hidden">
            <div className="flex animate-marquee w-max">
              {/* Original set */}
              <div className="flex gap-8 px-4">
                {pillars}
              </div>
              {/* Duplicate for seamless loop */}
              <div className="flex gap-8 px-4" aria-hidden="true">
                {pillars}
              </div>
            </div>
          </div>
        </div>

        {/* ── Founder ──────────────────────────────────── */}
        <div
          className={`mt-10 sm:mt-14 flex items-center justify-center gap-4 transition-all duration-700 delay-[900ms] ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
          }`}
        >
          <span className="w-8 h-px bg-vc2-hairline" />
          <span className="vc2-display text-sm text-vc2-ink-soft">{sede.founder?.name}</span>
          <span className="vc2-kicker text-[0.9rem] text-vc2-ink-soft">{copy.manifesto.founderLine}</span>
          <span className="w-8 h-px bg-vc2-hairline" />
        </div>
      </div>
    </section>
  )
}

/* ── Rose bouquet corner decoration ───────────────────────────────── */
function RoseBouquet({ className = '' }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 200 200"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={`opacity-[0.08] pointer-events-none ${className}`}
      aria-hidden="true"
    >
      {/* Stems */}
      <g stroke="rgb(201 168 117)" strokeWidth="1.5" strokeLinecap="round">
        <path d="M100 200 Q95 160, 80 130 Q70 110, 60 90" />
        <path d="M100 200 Q105 155, 120 125 Q130 105, 140 85" />
        <path d="M100 200 Q100 150, 100 110 Q100 90, 95 70" />
        <path d="M100 200 Q90 165, 55 135 Q40 125, 35 110" />
        <path d="M100 200 Q110 165, 145 135 Q160 125, 165 110" />
      </g>
      {/* Leaves */}
      <g fill="rgb(201 168 117)" opacity="0.5">
        <ellipse cx="72" cy="150" rx="12" ry="5" transform="rotate(-35 72 150)" />
        <ellipse cx="128" cy="148" rx="12" ry="5" transform="rotate(30 128 148)" />
        <ellipse cx="85" cy="120" rx="10" ry="4" transform="rotate(-50 85 120)" />
        <ellipse cx="115" cy="118" rx="10" ry="4" transform="rotate(45 115 118)" />
        <ellipse cx="50" cy="125" rx="11" ry="4.5" transform="rotate(-25 50 125)" />
        <ellipse cx="150" cy="123" rx="11" ry="4.5" transform="rotate(20 150 123)" />
        <ellipse cx="92" cy="95" rx="9" ry="3.5" transform="rotate(-40 92 95)" />
        <ellipse cx="108" cy="93" rx="9" ry="3.5" transform="rotate(35 108 93)" />
      </g>
      {/* Rose blooms — layered petals */}
      <g fill="rgb(201 168 117)">
        {/* Center rose */}
        <circle cx="95" cy="65" r="14" opacity="0.6" />
        <circle cx="90" cy="60" r="10" opacity="0.4" />
        <circle cx="100" cy="58" r="9" opacity="0.5" />
        <circle cx="93" cy="70" r="8" opacity="0.35" />
        {/* Left rose */}
        <circle cx="55" cy="88" r="12" opacity="0.6" />
        <circle cx="50" cy="84" r="8" opacity="0.4" />
        <circle cx="60" cy="83" r="7" opacity="0.5" />
        <circle cx="53" cy="93" r="6" opacity="0.35" />
        {/* Right rose */}
        <circle cx="145" cy="82" r="12" opacity="0.6" />
        <circle cx="140" cy="78" r="8" opacity="0.4" />
        <circle cx="150" cy="77" r="7" opacity="0.5" />
        <circle cx="143" cy="87" r="6" opacity="0.35" />
        {/* Small buds */}
        <circle cx="35" cy="105" r="7" opacity="0.5" />
        <circle cx="33" cy="102" r="4.5" opacity="0.35" />
        <circle cx="165" cy="103" r="7" opacity="0.5" />
        <circle cx="163" cy="100" r="4.5" opacity="0.35" />
      </g>
    </svg>
  )
}

/* ── Pillar ────────────────────────────────────────────────────────── */
function Pillar({ word, text }: { word: string; text: string }) {
  return (
    <div className="text-center flex flex-col items-center min-w-[200px] flex-shrink-0 snap-center sm:min-w-0 sm:flex-shrink">
      <span
        className="vc2-display text-vc2-accent leading-none mb-3"
        style={{
          fontSize: 'clamp(1.25rem, 2.5vw, 1.75rem)',
          letterSpacing: '0.03em',
          fontWeight: 400,
        }}
      >
        {word}
      </span>
      <span className="w-6 h-px bg-vc2-brass/40 mb-3" />
      <p className="text-vc2-ink-soft text-[13px] leading-relaxed max-w-[240px]">
        {text}
      </p>
    </div>
  )
}
